import { useState, type ReactNode, type MouseEvent } from 'react';

export type BadgeVariant = 'solid' | 'outline' | 'soft';
export type BadgeTone    = 'cyan' | 'amber' | 'danger' | 'success' | 'muted' | 'neutral';
export type BadgeSize    = 'sm' | 'md';

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  tone?: BadgeTone;
  size?: BadgeSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  title?: string;
}

// ── Colour tables ─────────────────────────────────────────────────────────────

type Clr = { bg: string; color: string; border: string };

const SOLID: Record<BadgeTone, Clr> = {
  cyan:    { bg: 'var(--evo-cyan)',    color: 'var(--evo-fg-on-cyan)',  border: 'transparent' },
  amber:   { bg: 'var(--evo-amber)',   color: 'var(--evo-bg)',          border: 'transparent' },
  danger:  { bg: 'var(--evo-danger)',  color: '#fff',                   border: 'transparent' },
  success: { bg: 'var(--evo-success)', color: 'var(--evo-bg)',          border: 'transparent' },
  muted:   { bg: 'var(--evo-bg-3)',    color: 'var(--evo-fg-2)',        border: 'transparent' },
  neutral: { bg: 'var(--evo-fg-2)',    color: 'var(--evo-bg)',          border: 'transparent' },
};

const OUTLINE: Record<BadgeTone, Clr> = {
  cyan:    { bg: 'transparent', color: 'var(--evo-cyan)',    border: 'var(--evo-cyan)'    },
  amber:   { bg: 'transparent', color: 'var(--evo-amber)',   border: 'var(--evo-amber)'   },
  danger:  { bg: 'transparent', color: 'var(--evo-danger)',  border: 'var(--evo-danger)'  },
  success: { bg: 'transparent', color: 'var(--evo-success)', border: 'var(--evo-success)' },
  muted:   { bg: 'transparent', color: 'var(--evo-fg-3)',    border: 'var(--evo-stroke)'  },
  neutral: { bg: 'transparent', color: 'var(--evo-fg-2)',    border: 'var(--evo-stroke)'  },
};

const SOFT: Record<BadgeTone, Clr> = {
  cyan:    { bg: 'var(--evo-cyan-12)',         color: 'var(--evo-cyan)',    border: 'transparent' },
  amber:   { bg: 'rgba(245,158,11,0.14)',      color: 'var(--evo-amber)',   border: 'transparent' },
  danger:  { bg: 'rgba(239,68,68,0.14)',       color: 'var(--evo-danger)',  border: 'transparent' },
  success: { bg: 'rgba(34,197,94,0.14)',       color: 'var(--evo-success)', border: 'transparent' },
  muted:   { bg: 'var(--evo-bg-3)',            color: 'var(--evo-fg-3)',    border: 'transparent' },
  neutral: { bg: 'var(--evo-panel)',           color: 'var(--evo-fg-2)',    border: 'transparent' },
};

const H:   Record<BadgeSize, string> = { sm: '18px', md: '22px' };
const PX:  Record<BadgeSize, string> = { sm: '8px',  md: '10px' };
const FS:  Record<BadgeSize, string> = { sm: '9px',  md: '11px' };
const GAP: Record<BadgeSize, number> = { sm: 4,      md: 5      };
const ICN: Record<BadgeSize, number> = { sm: 9,      md: 11     };

// ── Badge ─────────────────────────────────────────────────────────────────────

export function Badge({
  children,
  variant = 'outline',
  tone = 'neutral',
  size = 'sm',
  leftIcon,
  rightIcon,
  onClick,
  title,
}: BadgeProps) {
  const [hovered, setHovered] = useState(false);
  const clickable = onClick != null;

  const palette =
    variant === 'solid'   ? SOLID[tone] :
    variant === 'outline' ? OUTLINE[tone] :
                            SOFT[tone];

  const opacity = clickable && hovered ? 0.85 : 1;

  return (
    <span
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: GAP[size],
        height: H[size],
        padding: `0 ${PX[size]}`,
        fontSize: FS[size],
        fontWeight: 500,
        fontFamily: 'var(--evo-font)',
        lineHeight: 1,
        background: palette.bg,
        color: palette.color,
        border: variant === 'outline'
          ? `0.5px solid ${palette.border}`
          : palette.border === 'transparent'
            ? '0.5px solid transparent'
            : `0.5px solid ${palette.border}`,
        borderRadius: 'var(--evo-r-pill)',
        cursor: clickable ? 'pointer' : 'default',
        opacity,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        flexShrink: 0,
        transition: 'opacity 120ms linear, background 120ms linear, color 120ms linear',
        boxSizing: 'border-box',
      }}
    >
      {leftIcon != null && (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, width: ICN[size], height: ICN[size] }}>
          {leftIcon}
        </span>
      )}
      <span>{children}</span>
      {rightIcon != null && (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0, width: ICN[size], height: ICN[size] }}>
          {rightIcon}
        </span>
      )}
    </span>
  );
}
